import { useMemo } from 'react';
import { parseWorkflowNodeData } from '@cotera/api';
import { Ty } from '@cotera/era';
import { PersistedWorkflow } from '@cotera/sdk/core';
import {
  formatToolCallParamBindingRef,
  parseToolCallParamBindingRef,
  WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID,
} from '@cotera/utilities';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useToolsAsync } from '@cotera/client/app/hooks/use-tools-info';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import {
  useAgentOptions,
  useDatasetShape,
} from '@cotera/client/app/v1/trigger/hooks';
import {
  findWebhookProviderCatalogEntry,
  useWebhookProviderCatalog,
} from '@cotera/client/app/v1/webhook-providers';
import { resolveWebhookEventOutputFromCatalog } from '@cotera/client/app/v1/webhook-providers/hooks/use-webhook-event-output';
import { useResourceUpdatedWatchable } from '../../watchables/use-resource-updated-watchable';
import { listJsonPointerPaths } from '../components/node/edit-strategies/json-pointer-suggestions';
import { useWorkflowModel } from '../workflow.model';
import { useEmitEventPayloadFieldsByName } from './use-emit-event-name-suggestions';
import type { WorkflowGraphApiData } from '../workflow-graph.types';

const {
  PersistedWorkflowTriggerKind,
  buildWebhookRunInput,
  buildEventRunInput,
  buildCronRunInput,
  sampleDatasetRunInput,
  CRON_RUN_INPUT_SAMPLE_TIMESTAMP,
  buildWorkflowNodeOutputShapeContext,
  resolveWorkflowNodeOutputSample,
  workflowNodeFromGraphNode,
  workflowGraphAncestors,
} = PersistedWorkflow;

/** Payload field names each event carries, keyed by event name. */
export type EventPayloadFieldsByName = ReadonlyMap<string, readonly string[]>;

export type ToolCallUpstreamSample = {
  /** Stable name used in binding refs — must match the server's runtime keying. */
  readonly sourceName: string;
  /** Friendly label for display (e.g. an agent's title), falls back to sourceName. */
  readonly label: string;
  /**
   * Distance from the node being edited: `1` for a direct predecessor, `2`+ for
   * earlier ancestors. Used to split the picker into "Previous step" vs "Earlier
   * outputs", and to scope nodes that only read their direct input (filter,
   * for-each) back to direct predecessors.
   */
  readonly sourceDepth: number;
  readonly sample: unknown;
  readonly pointerSuggestions: readonly string[];
};

/** Friendly display names keyed by agent id (from the agents list). */
export type AgentTitleById = ReadonlyMap<string, string>;

function isEntryNode(graph: WorkflowGraphApiData, nodeId: string): boolean {
  const targets = new Set(graph.edges.map((edge) => edge.toNodeId));
  return !targets.has(nodeId);
}

function resolveWebhookTrigger(graph: WorkflowGraphApiData): {
  sourceName: string;
  providerKey: string;
  eventKey: string;
  defaultInput: unknown;
} | null {
  const binding =
    graph.triggerBindings.find((row) => row.enabled === true) ??
    graph.triggerBindings[0];
  if (binding === undefined) {
    return null;
  }
  const snapshot = graph.triggers.find(
    (row) => row.trigger.id === binding.triggerId
  );
  if (
    snapshot === undefined ||
    snapshot.kind !== PersistedWorkflowTriggerKind.Webhook
  ) {
    return null;
  }
  const providerKey = snapshot.trigger.providerKey;
  const eventKey = snapshot.trigger.eventKey;
  if (providerKey === undefined || eventKey === undefined) {
    return null;
  }
  return {
    sourceName: snapshot.trigger.name,
    providerKey,
    eventKey,
    defaultInput: binding.defaultInput ?? {},
  };
}

function resolveWebhookSample(
  providers: ReturnType<typeof useWebhookProviderCatalog>['providers'],
  context: {
    providerKey: string;
    eventKey: string;
    defaultInput: unknown;
  }
): unknown | null {
  const catalogEntry = findWebhookProviderCatalogEntry(
    providers,
    context.providerKey
  );
  if (catalogEntry === undefined) {
    return null;
  }
  const event = catalogEntry.events.find(
    (candidate) => candidate.key === context.eventKey
  );
  if (event?.samplePayload === undefined) {
    return null;
  }
  return buildWebhookRunInput(context.defaultInput, event.samplePayload);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Builds a representative event-trigger run input. Payload field names come from
 * the emitting node's `payloadBindings`; each is stubbed with an empty string so
 * the pointer walker exposes it. Even with no known fields the wrapper still
 * offers `eventName` / `eventPayload`, so the picker is never empty.
 */
function buildEventTriggerSample(
  defaultInput: unknown,
  eventName: string,
  payloadFields: readonly string[]
): unknown {
  const payload: Record<string, string> = {};
  for (const field of payloadFields) {
    payload[field] = '';
  }
  return buildEventRunInput(defaultInput, { eventName, payload });
}

/** Builds a representative cron-trigger run input (defaults merged with `timestamp`). */
function buildCronTriggerSample(
  defaultInput: unknown,
  workflowInput: unknown
): unknown {
  const base = isPlainObject(defaultInput) ? defaultInput : {};
  const triggerInput = isPlainObject(workflowInput) ? workflowInput : null;
  const merged = Object.keys(base).length > 0 ? base : triggerInput ?? {};
  return buildCronRunInput(merged, CRON_RUN_INPUT_SAMPLE_TIMESTAMP);
}

type EntryTriggerSample = {
  /** The trigger's own name, used as the binding source label. */
  readonly sourceName: string;
  readonly sample: unknown;
};

/**
 * Sample run input for whichever trigger fires an entry node — webhook, event,
 * or cron. This is what powers `/` and lightning-bolt field suggestions on the
 * first node of a workflow.
 */
/** Column names per dataset id, for a dataset trigger's row sample. */
export type DatasetColumnsById = ReadonlyMap<string, readonly string[]>;

function resolveEntryTriggerSample(
  graph: WorkflowGraphApiData,
  providers: ReturnType<typeof useWebhookProviderCatalog>['providers'],
  payloadFieldsByName: EventPayloadFieldsByName,
  datasetColumnsById: DatasetColumnsById
): EntryTriggerSample | null {
  const binding =
    graph.triggerBindings.find((row) => row.enabled === true) ??
    graph.triggerBindings[0];
  if (binding === undefined) {
    return null;
  }
  const snapshot = graph.triggers.find(
    (row) => row.trigger.id === binding.triggerId
  );
  if (snapshot === undefined) {
    return null;
  }

  if (snapshot.kind === PersistedWorkflowTriggerKind.Webhook) {
    const webhook = resolveWebhookTrigger(graph);
    if (webhook === null) {
      return null;
    }
    const sample = resolveWebhookSample(providers, webhook);
    if (sample === null) {
      return null;
    }
    return { sourceName: webhook.sourceName, sample };
  }

  if (snapshot.kind === PersistedWorkflowTriggerKind.Dataset) {
    const datasetId = snapshot.trigger.datasetId;
    const sourceName = snapshot.trigger.name ?? 'Dataset trigger';
    const knownColumns = datasetColumnsById.get(datasetId);
    // A table known to have no columns yet describes no row shape, so publish no
    // sample rather than a column-less one. A column-less sample reads as "this
    // row has no fields" and makes every ref to a column look broken, so a
    // mapper built on a not-yet-populated table lit up with an error on every
    // field. With no sample those refs are accepted (an unknown shape is
    // permissive), and the real sample takes over the moment columns exist.
    // Mirrors the server's validator, which declines the sample in this case.
    //
    // `undefined` (the schema hasn't loaded here yet) is left to fall through to
    // the metadata skeleton below, so an opening drawer isn't momentarily empty.
    if (knownColumns !== undefined && knownColumns.length === 0) {
      return { sourceName, sample: null };
    }
    return {
      sourceName,
      // Shared with the server's validator so the picker cannot offer a pointer
      // the validator would then reject.
      sample: sampleDatasetRunInput({
        defaultInput: binding.defaultInput,
        datasetId,
        columns: knownColumns ?? [],
      }),
    };
  }

  if (snapshot.kind === PersistedWorkflowTriggerKind.Event) {
    const eventName = snapshot.trigger.eventName;
    if (typeof eventName !== 'string' || eventName.trim() === '') {
      return null;
    }
    const trimmed = eventName.trim();
    const payloadFields = payloadFieldsByName.get(trimmed) ?? [];
    return {
      sourceName: snapshot.trigger.name,
      sample: buildEventTriggerSample(
        binding.defaultInput ?? {},
        trimmed,
        payloadFields
      ),
    };
  }

  if (snapshot.kind === PersistedWorkflowTriggerKind.Cron) {
    return {
      sourceName: snapshot.trigger.name,
      sample: buildCronTriggerSample(
        binding.defaultInput ?? {},
        snapshot.trigger.workflowInput
      ),
    };
  }

  return null;
}

/**
 * Every ancestor of `nodeId` (transitive predecessors), breadth-first so direct
 * predecessors come first, each tagged with its `depth` (1 = direct predecessor,
 * 2+ = earlier ancestor). A node can reference the output of ANY earlier node,
 * so the picker offers the full set. Shares {@link workflowGraphAncestors} with
 * server-side param-binding validation so both agree on the set + ordering.
 */
function ancestorNodeIds(
  graph: WorkflowGraphApiData,
  nodeId: string
): readonly { readonly id: string; readonly depth: number }[] {
  return workflowGraphAncestors(graph.edges, nodeId);
}

function resolveWebhookRunOutputShape(
  graph: WorkflowGraphApiData,
  providers: ReturnType<typeof useWebhookProviderCatalog>['providers']
): string | null {
  const binding =
    graph.triggerBindings.find((row) => row.enabled === true) ??
    graph.triggerBindings[0];
  if (binding === undefined) {
    return null;
  }
  const snapshot = graph.triggers.find(
    (row) => row.trigger.id === binding.triggerId
  );
  if (
    snapshot === undefined ||
    snapshot.kind !== PersistedWorkflowTriggerKind.Webhook
  ) {
    return null;
  }
  const providerKey = snapshot.trigger.providerKey;
  const eventKey = snapshot.trigger.eventKey;
  if (providerKey === undefined || eventKey === undefined) {
    return null;
  }
  const catalogEntry = findWebhookProviderCatalogEntry(providers, providerKey);
  const output = resolveWebhookEventOutputFromCatalog(catalogEntry, eventKey);
  return output.runOutputShape ?? output.outputShape;
}

/** Agent output shapes (`Ty.displayTy` strings) keyed by agent id. */
export type AgentOutputShapeById = ReadonlyMap<string, string>;

/** Stable empty value for a graph with no agent nodes — no fetch, no subscription. */
const NO_AGENT_OUTPUT_SHAPES: AgentOutputShapeById = new Map();

function buildOutputShapeContext(
  graph: WorkflowGraphApiData,
  providers: ReturnType<typeof useWebhookProviderCatalog>['providers'],
  tools: ReturnType<typeof useToolsAsync>['tools'],
  agentOutputShapeById: AgentOutputShapeById,
  payloadFieldsByName: EventPayloadFieldsByName,
  datasetColumnsById: DatasetColumnsById
): ReturnType<typeof buildWorkflowNodeOutputShapeContext> {
  const toolsByKey = new Map(
    tools.flatMap((tool) => [
      [tool.key, tool] as const,
      [tool.key.replaceAll('/', '_'), tool] as const,
    ])
  );

  return buildWorkflowNodeOutputShapeContext({
    nodes: graph.nodes,
    edges: graph.edges,
    resolveAgentOutputShape: (agentId) =>
      agentOutputShapeById.get(agentId) ?? null,
    resolveToolOutputShape: (toolKey) => {
      const tool =
        toolsByKey.get(toolKey) ?? toolsByKey.get(toolKey.replaceAll('/', '_'));
      if (tool === undefined) {
        return null;
      }
      return Ty.displayTy(tool.outputTy);
    },
    resolveWebhookRunOutputShape: () =>
      resolveWebhookRunOutputShape(graph, providers),
    // A mapper types each field off the sample its source publishes, and the
    // trigger is a source like any other — without this a field bound to
    // `__trigger__` would type as `super` and offer nothing to drill into on
    // the steps below the mapper.
    resolveTriggerSample: () =>
      resolveEntryTriggerSample(
        graph,
        providers,
        payloadFieldsByName,
        datasetColumnsById
      )?.sample ?? null,
  });
}

function resolveNodeSample(
  graph: WorkflowGraphApiData,
  node: WorkflowGraphApiData['nodes'][number],
  providers: ReturnType<typeof useWebhookProviderCatalog>['providers'],
  tools: ReturnType<typeof useToolsAsync>['tools'],
  agentOutputShapeById: AgentOutputShapeById,
  payloadFieldsByName: EventPayloadFieldsByName,
  datasetColumnsById: DatasetColumnsById
): unknown | null {
  const outputShapeContext = buildOutputShapeContext(
    graph,
    providers,
    tools,
    agentOutputShapeById,
    payloadFieldsByName,
    datasetColumnsById
  );
  const sample = resolveWorkflowNodeOutputSample(node, outputShapeContext);
  if (sample !== null) {
    return sample;
  }

  if (isEntryNode(graph, node.id)) {
    return (
      resolveEntryTriggerSample(
        graph,
        providers,
        payloadFieldsByName,
        datasetColumnsById
      )?.sample ?? null
    );
  }
  return null;
}

function pointerSuggestionsForSample(
  sourceName: string,
  sample: unknown
): readonly string[] {
  const paths = listJsonPointerPaths(sample);
  if (paths.length === 0) {
    return [formatToolCallParamBindingRef(sourceName, '')];
  }
  return paths.map((pointer) =>
    formatToolCallParamBindingRef(sourceName, pointer)
  );
}

/** An agent node's title, for the friendly display label. */
function agentTitleForNode(
  node: WorkflowGraphApiData['nodes'][number],
  agentTitleById: AgentTitleById
): string | undefined {
  if (node.type !== 'agent') {
    return undefined;
  }
  const parsed = parseWorkflowNodeData('agent', node.data);
  return parsed.t === 'valid'
    ? agentTitleById.get(parsed.data.agentId)
    : undefined;
}

export function buildToolCallUpstreamSamples(
  graph: WorkflowGraphApiData,
  nodeId: string,
  providers: ReturnType<typeof useWebhookProviderCatalog>['providers'],
  tools: ReturnType<typeof useToolsAsync>['tools'],
  agentOutputShapeById: AgentOutputShapeById,
  agentTitleById: AgentTitleById,
  payloadFieldsByName: EventPayloadFieldsByName,
  /** Column names per dataset, for building a dataset trigger's row sample. */
  datasetColumnsById: DatasetColumnsById = new Map()
): readonly ToolCallUpstreamSample[] {
  const predecessorIds = ancestorNodeIds(graph, nodeId);

  /**
   * The trigger's own payload, offered at `sourceDepth`.
   *
   * Reachable from every node, not just the first: the server keys the trigger
   * payload under the shared sentinel for any node the synthetic run-input node
   * is an ancestor of, so a binding to it resolves however deep the node sits.
   * Refs store stable ids, not display names, and the trigger has no graph node
   * — hence the sentinel rather than an id.
   */
  const triggerSample = (
    sourceDepth: number
  ): ToolCallUpstreamSample | null => {
    const trigger = resolveEntryTriggerSample(
      graph,
      providers,
      payloadFieldsByName,
      datasetColumnsById
    );
    if (trigger === null) {
      return null;
    }
    const binding =
      graph.triggerBindings.find((row) => row.enabled === true) ??
      graph.triggerBindings[0];
    if (binding === undefined) {
      return null;
    }
    const sourceName = WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID;
    return {
      sourceName,
      label: trigger.sourceName,
      sourceDepth,
      sample: trigger.sample,
      pointerSuggestions: pointerSuggestionsForSample(
        sourceName,
        trigger.sample
      ),
    };
  };

  if (predecessorIds.length === 0) {
    // Entry node — its only upstream is the trigger, treated as the direct step.
    const trigger = triggerSample(1);
    return trigger === null ? [] : [trigger];
  }

  const samples: ToolCallUpstreamSample[] = [];
  for (const { id: predecessorId, depth: sourceDepth } of predecessorIds) {
    const node = graph.nodes.find(
      (candidate) => candidate.id === predecessorId
    );
    if (node === undefined) {
      continue;
    }
    // The ref key is the node's stable id; the display label is resolved
    // separately (agent title, else the node's name).
    const sourceName = node.id;
    const label =
      agentTitleForNode(node, agentTitleById) ??
      workflowNodeFromGraphNode(node).name;
    const sample = resolveNodeSample(
      graph,
      node,
      providers,
      tools,
      agentOutputShapeById,
      payloadFieldsByName,
      datasetColumnsById
    );

    if (sample === null) {
      // The predecessor doesn't produce a typed output (e.g. an agent that
      // outputs a plain string). Expose the step itself as a single selectable
      // option so the user can bind the entire output to a tool property.
      const rootRef = formatToolCallParamBindingRef(sourceName, '');
      samples.push({
        sourceName,
        label,
        sourceDepth,
        sample: `{{${sourceName}}}`,
        pointerSuggestions: [rootRef],
      });
      continue;
    }

    samples.push({
      sourceName,
      label,
      sourceDepth,
      sample,
      pointerSuggestions: pointerSuggestionsForSample(sourceName, sample),
    });
  }

  // The thing that started the run is upstream of every node in it, so it
  // belongs in the picker for all of them — a step three deep still commonly
  // wants the row that triggered it. Placed past the furthest ancestor so it
  // reads as the earliest thing available rather than as the previous step.
  const furthest = predecessorIds.reduce(
    (deepest, { depth }) => Math.max(deepest, depth),
    0
  );
  const trigger = triggerSample(furthest + 1);
  if (trigger !== null) {
    samples.push(trigger);
  }
  return samples;
}

export function parseBindingRef(ref: string): {
  sourceName: string;
  pointer: string;
} {
  const parsed = parseToolCallParamBindingRef(ref);
  if (parsed.sourceName === undefined) {
    throw new Error(`Expected node-scoped binding, got "${ref}"`);
  }
  return { sourceName: parsed.sourceName, pointer: parsed.pointer };
}

/**
 * Upstream JSON samples for tool-call param bindings, keyed by predecessor display name.
 */
/** Distinct agent ids referenced by agent nodes in the graph. */
function agentIdsFromGraph(graph: WorkflowGraphApiData): readonly string[] {
  const ids = new Set<string>();
  for (const node of graph.nodes) {
    if (node.type !== 'agent') {
      continue;
    }
    const parsed = parseWorkflowNodeData('agent', node.data);
    if (parsed.t === 'valid' && parsed.data.agentId !== '') {
      ids.add(parsed.data.agentId);
    }
  }
  return [...ids];
}

/**
 * Everything `buildToolCallUpstreamSamples` needs, gathered once.
 *
 * Split out from the per-node hook below because two callers want it for two
 * different scopes: the open drawer, for the one node being edited, and the
 * canvas, for every node at once. Gathering it per node would have meant a
 * subscription and a fetch per node on a graph that already has all the answers
 * in hand.
 */
export type WorkflowUpstreamSampleContext = {
  readonly graph: WorkflowGraphApiData | null;
  readonly providers: ReturnType<typeof useWebhookProviderCatalog>['providers'];
  readonly tools: ReturnType<typeof useToolsAsync>['tools'];
  readonly agentOutputShapeById: AgentOutputShapeById;
  readonly agentTitleById: AgentTitleById;
  readonly payloadFieldsByName: EventPayloadFieldsByName;
  readonly datasetColumnsById: DatasetColumnsById;
  readonly catalogLoading: boolean;
  readonly toolsLoading: boolean;
};

export function useWorkflowUpstreamSampleContext(): WorkflowUpstreamSampleContext {
  const model = useWorkflowModel();
  const graph = useWatchableValue(model.graphWatchable);
  const { providers, isLoading: catalogLoading } = useWebhookProviderCatalog();
  const { tools, isLoading: toolsLoading } = useToolsAsync();
  const client = useTenantedClient();
  const agents = useAgentOptions();
  const payloadFieldsByName = useEmitEventPayloadFieldsByName();
  // A dataset trigger's run input carries the row's own columns, so the picker
  // needs the dataset's schema to offer them. Live rather than fetched once: a
  // column added in another tab has to reach an open drawer.
  const datasetTriggerId = useMemo(() => {
    if (graph === null) {
      return null;
    }
    const binding =
      graph.triggerBindings.find((row) => row.enabled === true) ??
      graph.triggerBindings[0];
    const snapshot =
      binding === undefined
        ? undefined
        : graph.triggers.find((row) => row.trigger.id === binding.triggerId);
    if (
      snapshot === undefined ||
      snapshot.kind !== PersistedWorkflowTriggerKind.Dataset
    ) {
      return null;
    }
    return snapshot.trigger.datasetId === ''
      ? null
      : snapshot.trigger.datasetId;
  }, [graph]);
  const datasetTriggerShape = useDatasetShape(datasetTriggerId);
  const datasetColumnsById = useMemo<DatasetColumnsById>(
    () =>
      datasetTriggerId === null || datasetTriggerShape === null
        ? new Map()
        : new Map([[datasetTriggerId, datasetTriggerShape.columns]]),
    [datasetTriggerId, datasetTriggerShape]
  );
  const agentTitleById = useMemo<AgentTitleById>(() => {
    const map = new Map<string, string>();
    for (const agent of agents) {
      if (typeof agent.title === 'string' && agent.title.trim() !== '') {
        map.set(agent.id, agent.title);
      }
    }
    return map;
  }, [agents]);

  // Agent nodes don't carry their response shape in the graph, so read each
  // referenced agent's output type. These power "pick a field from the agent's
  // response" in the tool node editor.
  const agentIds = useMemo(
    () => (graph === null ? [] : agentIdsFromGraph(graph)),
    [graph]
  );
  // Live, like the dataset columns above and for the same reason: an agent's
  // output shape is edited in its own artifact, in another tab, while this
  // drawer stays open. Behind a cached fetch the picker went on offering the
  // fields the agent used to return — and the ref check added since would have
  // called a ref to one of the new ones broken.
  const agentIdsKey = agentIds.join('|');
  const agentOutputShapeById =
    useResourceUpdatedWatchable<AgentOutputShapeById>(
      `workflow-agent-output-shapes:${agentIdsKey}`,
      (factory) => factory.makeAgentOutputShapesWatchable({ agentIds, client }),
      { enabled: agentIds.length > 0, disabledValue: NO_AGENT_OUTPUT_SHAPES }
    );

  return {
    graph,
    providers,
    tools,
    agentOutputShapeById,
    agentTitleById,
    payloadFieldsByName,
    datasetColumnsById,
    catalogLoading,
    toolsLoading,
  };
}

export function useToolCallUpstreamSamples(nodeId: string | undefined): {
  upstreamSamples: readonly ToolCallUpstreamSample[];
  isLoading: boolean;
} {
  const {
    graph,
    providers,
    tools,
    agentOutputShapeById,
    agentTitleById,
    payloadFieldsByName,
    datasetColumnsById,
    catalogLoading,
    toolsLoading,
  } = useWorkflowUpstreamSampleContext();

  const upstreamSamples = useMemo(() => {
    if (graph === null || nodeId === undefined) {
      return [];
    }
    return buildToolCallUpstreamSamples(
      graph,
      nodeId,
      providers,
      tools,
      agentOutputShapeById,
      agentTitleById,
      payloadFieldsByName,
      datasetColumnsById
    );
  }, [
    graph,
    nodeId,
    providers,
    tools,
    agentOutputShapeById,
    agentTitleById,
    payloadFieldsByName,
    datasetColumnsById,
  ]);

  const needsCatalog =
    graph !== null &&
    nodeId !== undefined &&
    (isEntryNode(graph, nodeId) ||
      ancestorNodeIds(graph, nodeId).some(({ id: ancestorId }) => {
        const node = graph.nodes.find(
          (candidate) => candidate.id === ancestorId
        );
        return node !== undefined && isEntryNode(graph, node.id);
      }));

  // Agent shapes are absent from this: the watchable reports no loading state,
  // and an empty map is not tellable from "no agent here declares a shape".
  // Nothing downstream misreads the gap — an agent whose shape has not landed
  // is offered as a single whole-output option, which is also what it is
  // offered as when it genuinely has no shape.
  return {
    upstreamSamples,
    isLoading: (catalogLoading && needsCatalog) || toolsLoading,
  };
}
